<template>
	<transition name="modal" class="sm terms-of-use">
		<section
			v-if="!acceptTermsError"
			class="terms-of-use global-modal"
			:clickToClose="false"
		>
			<section class="modal-dialog" role="document" @click.stop>
				<section class="modal-wrapper">
					<header class="modal-header">
						<span class="modal-title" role="heading">{{
							TERMS_OF_USE.TITLE
						}}</span>
						<button
							class="b360-modal-close-btn"
							aria-label="Close modal"
							@click="closeModalClickHandler"
						>
							<font-awesome-icon :icon="['far', 'times-circle']" />
						</button>
					</header>
					<section class="modal-sub-heading">
						<span class="sub-heading-content">{{
							TERMS_OF_USE.SUBHEADING
						}}</span>
					</section>
					<section class="modal-content">
						<TermsOfUseSlotComponent slot="modalContent" />
					</section>
					<footer class="modal-footer">
						<div class="user-action-btns">
							<BaseButton
								color="white"
								width="auto"
								aria-label="Cancel to close modal and return to UWM.com"
								@onClick="termsCancelledClickHandler"
								>Cancel
							</BaseButton>
							<BaseButton
								color="blue"
								:status="acceptButtonStatus"
								width="auto"
								aria-label="Accept to close modal and proceed"
								:disabled="acceptButtonStatus !== null"
								@onClick="termsAcceptedClickHandler"
								data-test="acceptButton"
								>Accept and Continue
							</BaseButton>
						</div>
						<button
							id="walkme-trigger-btn"
							class="walkme-trigger-start"
							ref="walkmeTriggerButton"
						></button>
					</footer>
				</section>
			</section>
		</section>
		<section
			v-if="acceptTermsError"
			class="terms-of-use global-modal"
			:clickToClose="false"
		>
			<section class="modal-dialog" role="document" @click.stop>
				<section class="modal-wrapper">
					<header class="modal-header">
						<span class="modal-title" role="heading">{{
							TERMS_OF_USE.ERROR_TITLE
						}}</span>
						<button
							class="b360-modal-close-btn"
							aria-label="Close modal"
							@click="closeModalClickHandler"
						>
							<font-awesome-icon :icon="['far', 'times-circle']" />
						</button>
					</header>
					<section>
						<FallBack
							:message="TERMS_OF_USE.ERROR_MESSAGE"
							:border="false"
							iconStyle="fal"
							icon="exclamation-triangle"
						/>
					</section>
					<footer class="modal-footer">
						<div class="user-action-btns">
							<BaseButton
								color="white"
								width="auto"
								aria-label="Cancel to close modal and return to UWM.com"
								@onClick="termsCancelledClickHandler"
								>Cancel
							</BaseButton>
							<BaseButton
								color="blue"
								width="auto"
								aria-label="Try Accepting Terms and Conditions Again"
								@onClick="continueButtonHandler"
								data-test="tryAgainButton"
								>Try Again
							</BaseButton>
						</div>
					</footer>
				</section>
			</section>
		</section>
	</transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import firstTimeUserService from '@/services/toolbox/first-time-user';
import leadsService from '@/services/contacts/leads-service.js';

import BaseButton from '@/components/global/BaseButton.vue';
import FallBack from '@/components/global/FallBack.vue';
import TermsOfUseSlotComponent from './TermsOfUseSlotComponent.vue';
import { TERMS_OF_USE } from './constants.js';

export default {
	name: 'TermsOfUse',
	components: {
		BaseButton,
		FallBack,
		TermsOfUseSlotComponent,
	},
	data() {
		return {
			TERMS_OF_USE,
			acceptTermsError: false,
			waiting: false,
			success: false,
			acceptButtonStatus: null,
		};
	},
	computed: {
		...mapGetters(['currentUserProfile']),
	},
	methods: {
		...mapActions(['updateUserTermsOfUseAccepted']),
		async termsAcceptedClickHandler() {
			this.acceptButtonStatus = 'waiting';
			try {
				const response = await firstTimeUserService.acceptTermsOfUseFirstTime();
				if (response && response.data === true) {
					this.acceptButtonStatus = 'success';
					setTimeout(() => {
						// delay so user sees feedback (ie checkmark)
						this.updateUserTermsOfUseAccepted();
						this.$refs.walkmeTriggerButton.click();
					}, 1500);

					// For /Lead/SaveOwner
					const {
						entityId,
						contactId,
						userId,
						firstName,
						lastName,
						middleName,
						preferredName,
					} = this.currentUserProfile;
					const payload = {
						entityId,
						contactId,
						userId,
						firstName,
						lastName,
						middleName,
						preferredName,
					};
					leadsService.saveOwner(payload);
				} else {
					this.acceptTermsError = true;
					this.acceptButtonStatus = null;
				}
			} catch (error) {
				this.acceptTermsError = true;
				this.acceptButtonStatus = null;
			}
		},
		termsCancelledClickHandler() {
			window.location.href = 'https://www.uwm.com';
		},
		continueButtonHandler() {
			this.acceptTermsError = false;
		},
		closeModalClickHandler() {
			window.location.href = 'https://www.uwm.com';
		},
	},
};
</script>

<style lang="scss" scoped>
.global-modal {
	position: fixed;
	z-index: 9998;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba($black, 0.5);
	display: block;
	transition: opacity 0.15s linear;
	overflow-y: auto;
}
.modal-dialog {
	max-width: 1280px;
	width: 80vw;
	display: block;
	top: 50px;
	position: relative;
	margin: 0 auto;

	@include mobile {
		width: calc(100vw - 30px);
	}

	.modal-wrapper {
		background-color: $off-white;
		border-radius: 5px;
		box-shadow: 0 0 20px 0 rgba($black, 0.15);
		position: relative;
		height: 90vh;
		display: flex;
		flex-direction: column;
	}

	header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: 30px;
		background-color: $white;
		border-radius: 5px 5px 0 0;
		box-shadow: inset 0 -1px 0 0 $border-color-light;
		padding: 15px;
		color: $text-base-color;
		@include typo--roboto__medium;
		font-size: $b360-font-size--large;

		.b360-modal-close-btn {
			background-color: transparent;
			border: 0;
			height: 24px;
			color: $brand-blue;
			cursor: pointer;
			font-size: $b360-font-size--larger;
		}
	}

	.modal-sub-heading {
		padding: 10px 0 8px 20px;
		@include typo--roboto__regular;
		font-size: $b360-font-size--small;

		@include mobile {
			width: calc(100vw - 30px);
		}
	}

	.modal-content {
		padding: 5px 15px 10px 15px;
		border: $b360-border-width-thin solid $border-color-light;
		background-color: $white;
		margin: 1px 20px 33px 20px;
		border-radius: $b360-border-radius-base;
		width: auto;
		height: 75vh;
		overflow: auto;
	}

	footer {
		background-color: $white;
		min-height: 45px;
		border-radius: 0 0 5px 5px;
		box-shadow: inset 0px 1px 0px 0px $border-color-light;
		padding: 15px;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;

		.user-action-btns {
			display: flex;
			flex-wrap: wrap;
			margin-left: auto;

			@include mobile {
				width: 100%;
			}

			button {
				display: flex;
				justify-content: center;
				align-items: center;
				line-height: 1;
				@include rem('min-width', 100px);
				@include rem('height', 44px);
				@include rem('padding', 12px);
				@include rem('margin-left', 15px);
			}

			button:first-child {
				margin-left: 0;
			}
		}
	}
}

.sm {
	.modal-dialog {
		max-width: 480px;
	}
}

.md {
	.modal-dialog {
		max-width: 600px;
	}
}

.lg {
	.modal-dialog {
		max-width: 920px;
	}
}

.modal-enter,
.modal-leave-to {
	opacity: 0;
	transition: opacity 0.5s ease-out;
}

.modal-enter .modal-dialog {
	transform: translate(0, 50px);
	transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

.modal-leave-to .modal-dialog {
	transform: translate(0, -50px);
	opacity: 0;
	transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

.walkme-trigger-start {
	display: none;
}
</style>
